import React, { useMemo, useState } from 'react';
import { XMarkIcon, PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { apiPost } from '../../utils/api';
import { normalizeCropName } from '../../utils/cropCatalog';

interface CreateCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingCropTypes?: string[];
}

const SUGGESTED_CROPS = [
  'Avocado',
  'Maize',
  'Beans',
  'Mango',
  'Macadamia',
  'Banana',
  'Coffee',
  'Tomato',
  'Carrots',
  'Irish Potato',
];

const CreateCropModal: React.FC<CreateCropModalProps> = ({ isOpen, onClose, onSuccess, existingCropTypes = [] }) => {
  const [form, setForm] = useState({
    product_type: '',
    variety: '',
    category: '',
    harvest_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const suggestions = useMemo(() => {
    const all = Array.from(new Set([...SUGGESTED_CROPS, ...existingCropTypes.map((t) => normalizeCropName(t)).filter(Boolean)]))
      .sort((a, b) => a.localeCompare(b));
    return all;
  }, [existingCropTypes]);

  const normalized = normalizeCropName(form.product_type);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!normalized) {
      setError('Crop type is required.');
      return;
    }

    setSubmitting(true);
    try {
      await apiPost('/api/crops', {
        product_type: normalized,
        variety: form.variety.trim() || null,
        category: form.category.trim() || null,
        harvest_date: form.harvest_date || null,
        notes: form.notes.trim() || null,
      });

      onSuccess();
      onClose();
      setForm({ product_type: '', variety: '', category: '', harvest_date: '', notes: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create crop');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Create New Crop</h2>
            <p className="text-green-100 text-xs">Add a crop type to the farm catalogue</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-2">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Crop Type *</label>
            <input
              type="text"
              name="product_type"
              value={form.product_type}
              onChange={handleChange}
              list="crop-type-options"
              placeholder="e.g., Avocado, Maize, Beans"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <datalist id="crop-type-options">
              {suggestions.map((crop) => (
                <option key={crop} value={crop} />
              ))}
            </datalist>
            {normalized && (
              <p className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                <SparklesIcon className="w-4 h-4" />
                Saved as “{normalized}”{normalized !== form.product_type.trim() ? ' (name normalized)' : ''}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Variety</label>
              <input
                type="text"
                name="variety"
                value={form.variety}
                onChange={handleChange}
                placeholder="e.g., Hass, G2"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                <option value="">Select category</option>
                <option value="Fruit">Fruit</option>
                <option value="Vegetable">Vegetable</option>
                <option value="Cereal">Cereal</option>
                <option value="Legume">Legume</option>
                <option value="Tuber">Tuber</option>
                <option value="Nut">Nut</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Harvest Date</label>
              <input
                type="date"
                name="harvest_date"
                value={form.harvest_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Optional notes about this crop..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !normalized}
            className="px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            {submitting ? 'Creating...' : 'Create Crop'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCropModal;
